import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { SidebarModule } from 'primeng/sidebar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageType, UtilsService } from 'src/app/common';
import { ActionButton, ActionButtonsComponent } from 'src/app/components/shared/action-buttons/action-buttons.component';
import { ConfirmationDialogComponent } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import {
    AdminOrganization,
    AdminOrganizationAuditDetail,
    AdminOrganizationAuditLog,
    AdminOrganizationMember,
    AdminUsageOperation,
    AdminUsageSummary
} from '../../config';
import { AdminPanelService } from '../../services/admin-panel.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-organizations',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        InputTextModule,
        PaginatorModule,
        SidebarModule,
        ButtonModule,
        CardModule,
        TagModule,
        TooltipModule,
        ActionButtonsComponent,
        ConfirmationDialogComponent
    ],
    templateUrl: './organizations.component.html',
    styleUrl: './organizations.component.scss'
})
export class OrganizationsComponent implements OnInit, OnDestroy {

    public organizations: AdminOrganization[] = [];
    public selectedOrganization: AdminOrganization | null = null;
    public organizationMembers: AdminOrganizationMember[] = [];

    public organizationsLoading = false;
    public membersLoading = false;
    public showMembersView = false;
    public showOrganizationDetailsDrawer = false;
    public showAuditView = false;
    public showAuditDetailView = false;
    public organizationSearchTerm = '';
    public organizationPage = 1;
    public organizationPageSize = 100;
    public organizationTotalRecords = 0;
    public memberSearchTerm = '';
    public memberPage = 1;
    public memberPageSize = 10;
    public memberTotalRecords = 0;
    public organizationAuditLogs: AdminOrganizationAuditLog[] = [];
    public organizationAuditTotalRecords = 0;
    public organizationAuditPage = 1;
    public organizationAuditPageSize = 10;
    public organizationAuditLoading = false;
    public selectedOrganizationAuditLog: AdminOrganizationAuditDetail | null = null;
    public organizationAuditDetailLoading = false;
    public usageSummary: AdminUsageSummary | null = null;
    public usageOperations: AdminUsageOperation[] = [];
    public usageLoading = false;
    public usageError: string | null = null;
    public usagePage = 1;
    public usagePageSize = 10;
    public usageTotalRecords = 0;

    private readonly memberSearchSubject = new Subject<string>();
    private readonly organizationSearchSubject = new Subject<string>();
    private readonly destroy$ = new Subject<void>();
    private usageRequestId = 0;

    public organizationToDelete: AdminOrganization | null = null;
    public organizationDeleteMode: 'soft' | 'hard' = 'soft';
    public showOrganizationDeleteConfirmDialog = false;

    public memberToDelete: AdminOrganizationMember | null = null;
    public showMemberDeleteConfirmDialog = false;
    public showBillingOwnerWarningDialog = false;
    public billingOwnerWarningMember: AdminOrganizationMember | null = null;
    public memberDeleteConfirmationInput = '';
    public hardDeleteConfirmationInput = '';

    public memberDeleteActionButtons: ActionButton[] = [
        {
            label: 'Cancel',
            type: 'cancel',
            outlined: true
        },
        {
            label: 'Confirm Delete',
            type: 'confirm',
            color: 'danger',
            disabled: true
        }
    ];

    public billingOwnerWarningButtons: ActionButton[] = [
        {
            label: 'Close',
            type: 'cancel',
            outlined: true
        }
    ];

    public getMemberDeleteButtons(): ActionButton[] {
        return [
            {
                label: 'Delete',
                type: 'delete',
                color: 'danger',
                outlined: true,
                size: 'small'
            }
        ];
    }

    public refreshMemberDeleteActionButtons(): void {
        const confirmationMatches = this.memberDeleteConfirmationInput.trim().toUpperCase() === 'CONFIRM';
        this.memberDeleteActionButtons = this.memberDeleteActionButtons.map(button => {
            if (button.type !== 'confirm') {
                return button;
            }

            return {
                ...button,
                disabled: !confirmationMatches
            };
        });
    }

    public handleMemberAction(event: any, member: AdminOrganizationMember): void {
        if (!event?.type) {
            return;
        }

        if (event.type === 'delete') {
            this.openMemberDeleteDialog(member);
        }
    }

    public isBillingOwner(member: AdminOrganizationMember): boolean {
        return member?.role?.trim().toLowerCase() === 'billingowner';
    }

    public getMemberDeleteMessage(): string {
        return this.memberToDelete
            ? `This action will permanently remove ${this.memberToDelete.name || 'this member'} from the organization. Type CONFIRM to proceed.`
            : 'This action will permanently remove the selected member from the organization. Type CONFIRM to proceed.';
    }

    public get billingOwnerWarningMessage(): string {
        return this.billingOwnerWarningMember
            ? `${this.billingOwnerWarningMember.name || this.billingOwnerWarningMember.email || 'This member'} has the BillingOwner role and cannot be deleted.`
            : 'This member has the BillingOwner role and cannot be deleted.';
    }

    public openBillingOwnerWarningDialog(member: AdminOrganizationMember): void {
        this.billingOwnerWarningMember = member;
        this.showBillingOwnerWarningDialog = true;
    }

    public handleBillingOwnerWarningDialog(event: { type?: string }): void {
        if (event?.type === 'cancel') {
            this.closeBillingOwnerWarningDialog();
        }
    }

    private closeBillingOwnerWarningDialog(): void {
        this.showBillingOwnerWarningDialog = false;
        this.billingOwnerWarningMember = null;
    }

    public organizationDeleteActionButtons: ActionButton[] = [
        {
            label: 'Cancel',
            type: 'cancel',
            outlined: true
        },
        {
            label: 'Continue',
            type: 'confirm',
            color: 'danger',
            disabled: false
        }
    ];

    public refreshOrganizationDeleteActionButtons(): void {
        const shouldDisableConfirm =
            this.organizationDeleteMode === 'hard' &&
            this.hardDeleteConfirmationInput.trim().toUpperCase() !== 'CONFIRM';

        this.organizationDeleteActionButtons = this.organizationDeleteActionButtons.map(button => {
            if (button.type !== 'confirm') {
                return button;
            }

            return {
                ...button,
                disabled: shouldDisableConfirm
            };
        });
    }

    private readonly adminPanelService = inject(AdminPanelService);
    private readonly utils = inject(UtilsService);

    public async ngOnInit(): Promise<void> {
        this.organizationSearchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.organizationPage = 1;
            void this.loadOrganizations(true);
        });

        this.memberSearchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            if (!this.selectedOrganization?.id) {
                return;
            }

            this.memberPage = 1;
            void this.loadOrganizationMembers(this.selectedOrganization.id, true);
        });

        await this.loadOrganizations();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.memberSearchSubject.complete();
        this.organizationSearchSubject.complete();
    }

    public async loadOrganizations(force = false): Promise<void> {
        this.organizationsLoading = true;

        try {
            const response = await this.adminPanelService.getPagedOrganizations({
                page: this.organizationPage,
                pageSize: this.organizationPageSize,
                q: this.organizationSearchTerm,
                force,
                handleError: true
            });
            this.organizations = response.data?.items ?? [];
            this.organizationTotalRecords = response.data?.totalCount ?? this.organizations.length;

            if (response.data?.pageNumber) {
                this.organizationPage = response.data.pageNumber;
            }

            if (response.data?.pageSize) {
                this.organizationPageSize = response.data.pageSize;
            }

            if (!this.organizations.length) {
                this.selectedOrganization = null;
                this.organizationMembers = [];
                this.showMembersView = false;
                this.showAuditView = false;
                this.showAuditDetailView = false;
                return;
            }

            const retainedOrganizationId = this.selectedOrganization?.id;
            const nextOrganization = this.organizations.find(org => org.id === retainedOrganizationId) ?? this.organizations[0];
            await this.selectOrganization(nextOrganization, force);
        } finally {
            this.organizationsLoading = false;
        }
    }

    public async onOrganizationPage(event: { page?: number; rows?: number }): Promise<void> {
        this.organizationPage = (event?.page ?? 0) + 1;
        this.organizationPageSize = event?.rows && event.rows > 0 ? event.rows : this.organizationPageSize;
        await this.loadOrganizations(true);
    }

    public onOrganizationSearchTermChange(value: string): void {
        this.organizationSearchTerm = value;
        this.organizationSearchSubject.next(value);
    }

    public async selectOrganization(organization: AdminOrganization, force = false): Promise<void> {
        if (!organization?.id) {
            return;
        }

        this.selectedOrganization = organization;
        this.memberSearchTerm = '';
        this.memberPage = 1;
        this.usagePage = 1;
        await Promise.all([
            this.loadOrganizationMembers(organization.id, force),
            this.loadOrganizationUsage(organization.id)
        ]);
    }

    public async loadOrganizationUsage(orgId: number): Promise<void> {
        if (!orgId) {
            return;
        }

        const requestId = ++this.usageRequestId;
        this.usageLoading = true;
        this.usageError = null;
        this.usageSummary = null;
        this.usageOperations = [];
        this.usageTotalRecords = 0;
        try {
            const [summaryResponse, operationsResponse] = await Promise.all([
                this.adminPanelService.getAdminUsageSummary(orgId),
                this.adminPanelService.getAdminUsageOperations(orgId, {
                    page: this.usagePage,
                    pageSize: this.usagePageSize
                })
            ]);
            if (requestId !== this.usageRequestId || this.selectedOrganization?.id !== orgId) {
                return;
            }

            this.usageSummary = summaryResponse.data;
            this.usageOperations = operationsResponse.data?.items ?? [];
            this.usageTotalRecords = operationsResponse.data?.totalCount ?? 0;
            if (!summaryResponse.success || !operationsResponse.success) {
                this.usageError = 'Authoritative usage is unavailable for this organization.';
            }
        } finally {
            if (requestId === this.usageRequestId) {
                this.usageLoading = false;
            }
        }
    }

    public async onUsagePage(event: { page?: number; rows?: number }): Promise<void> {
        if (!this.selectedOrganization?.id) {
            return;
        }

        this.usagePage = (event.page ?? 0) + 1;
        this.usagePageSize = event.rows ?? this.usagePageSize;
        await this.loadOrganizationUsage(this.selectedOrganization.id);
    }

    public getUsageModeSeverity(mode: AdminUsageSummary['mode']): 'success' | 'warning' | 'danger' | 'info' {
        if (mode === 'Authoritative') {
            return 'success';
        }

        if (mode === 'DualWrite') {
            return 'warning';
        }

        return mode === 'Observe' ? 'info' : 'danger';
    }

    public async loadOrganizationMembers(orgId: number, force = false): Promise<void> {
        if (!orgId) {
            return;
        }

        this.membersLoading = true;

        try {
            const response = await this.adminPanelService.getOrganizationMembers(orgId, {
                force,
                handleError: true,
                q: this.memberSearchTerm,
                page: this.memberPage,
                pageSize: this.memberPageSize
            });

            this.organizationMembers = response.data ?? [];
            this.memberTotalRecords = response.totalCount ?? this.organizationMembers.length;
        } finally {
            this.membersLoading = false;
        }
    }

    public onMemberSearchTermChange(value: string): void {
        this.memberSearchTerm = value;
        this.memberSearchSubject.next(value);
    }

    public async onMemberPage(event: any): Promise<void> {
        if (!this.selectedOrganization?.id) {
            return;
        }

        this.memberPage = event.page + 1;
        this.memberPageSize = event.rows;
        await this.loadOrganizationMembers(this.selectedOrganization.id, true);
    }

    public async reloadSelectedOrganizationMembers(): Promise<void> {
        if (!this.selectedOrganization) {
            return;
        }

        await this.selectOrganization(this.selectedOrganization, true);
    }

    public get filteredOrganizations(): AdminOrganization[] {
        return this.organizations;
    }

    public async openOrganizationMembers(organization: AdminOrganization, force = false): Promise<void> {
        this.showMembersView = true;
        this.showAuditView = false;
        this.showAuditDetailView = false;
        await this.selectOrganization(organization, force);
    }

    public openOrganizationDetails(organization: AdminOrganization): void {
        if (!organization?.id) {
            return;
        }

        this.selectedOrganization = organization;
        this.showOrganizationDetailsDrawer = true;
    }

    public closeOrganizationDetails(): void {
        this.showOrganizationDetailsDrawer = false;
    }

    public getOrganizationDetailsRows(organization: AdminOrganization | null): Array<{ label: string; value: string }> {
        if (!organization) {
            return [];
        }

        return [
            { label: 'Organization ID', value: this.getDisplayValue(organization.id) },
            { label: 'Name', value: this.getDisplayValue(organization.name) },
            { label: 'Email', value: this.getDisplayValue(organization.email) },
            { label: 'Phone Number', value: this.getDisplayValue(organization.phoneNumber) },
            { label: 'Slug', value: this.getDisplayValue(organization.slug) },
            { label: 'Timezone', value: this.getDisplayValue(organization.defaultTimezone) },
            { label: 'Default Invite Role', value: this.getDisplayValue(organization.defaultInviteRole) },
            { label: 'Subscription Status', value: this.getDisplayValue(this.getOrganizationStatus(organization)) },
            { label: 'Billing Interval', value: this.getDisplayValue(organization.subscriptionInterval) },
            { label: 'Subscription End', value: this.getDisplayValue(organization.subscriptionEndDate, true) },
            { label: 'Website', value: this.getDisplayValue(organization.website) },
            { label: 'Personal Organization', value: organization.isPersonal ? 'Yes' : 'No' },
            { label: 'Created Date', value: this.getDisplayValue(organization.createdDate, true) },
            { label: 'Modified Date', value: this.getDisplayValue(organization.modifiedDate, true) }
        ];
    }

    public getOrganizationAddressDisplay(organization: AdminOrganization | null): string {
        if (!organization?.address) {
            return 'Not provided';
        }

        const addressParts = [
            organization.address.street,
            organization.address.street1,
            organization.address.city,
            organization.address.state,
            organization.address.zip
        ].filter((value): value is string => !!value && value.trim().length > 0);

        return addressParts.length ? addressParts.join(', ') : 'Not provided';
    }

     public async openOrganizationAuditLogs(organization: AdminOrganization): Promise<void> {
        if (!organization?.id) {
            return;
        }

        this.selectedOrganization = organization;
        this.selectedOrganizationAuditLog = null;
        this.organizationAuditPage = 1;
        this.showMembersView = true;
        this.showAuditView = true;
        this.showAuditDetailView = false;
        await this.loadOrganizationAuditLogs(organization.id, true);
    }

    public backToMembersFromAudit(): void {
        this.showAuditView = false;
        this.showAuditDetailView = false;
        this.selectedOrganizationAuditLog = null;
    }

    public async loadOrganizationAuditLogs(orgId: number, force = false): Promise<void> {
        if (!orgId) {
            return;
        }

        this.organizationAuditLoading = true;
        try {
            const response = await this.adminPanelService.getOrganizationAuditLogs(orgId, {
                force,
                handleError: true,
                page: this.organizationAuditPage,
                pageSize: this.organizationAuditPageSize
            });

            this.organizationAuditLogs = response.data ?? [];
            this.organizationAuditTotalRecords = response.totalCount ?? this.organizationAuditLogs.length;
        } finally {
            this.organizationAuditLoading = false;
        }
    }

    public async onOrganizationAuditPage(event: { page?: number; rows?: number }): Promise<void> {
        if (!this.selectedOrganization?.id) {
            return;
        }

        this.organizationAuditPage = (event?.page ?? 0) + 1;
        this.organizationAuditPageSize = event?.rows && event.rows > 0 ? event.rows : this.organizationAuditPageSize;
        await this.loadOrganizationAuditLogs(this.selectedOrganization.id, true);
    }

    public async viewOrganizationAuditDetails(audit: AdminOrganizationAuditLog): Promise<void> {
        if (!this.selectedOrganization?.id || !audit?.id) {
            return;
        }

        this.showAuditDetailView = true;
        this.organizationAuditDetailLoading = true;
        this.selectedOrganizationAuditLog = null;
        try {
            const response = await this.adminPanelService.getOrganizationAuditLogDetail(this.selectedOrganization.id, audit.id, {
                handleError: true
            });

            this.selectedOrganizationAuditLog = response.data;
        } finally {
            this.organizationAuditDetailLoading = false;
        }
    }

    public backToAuditLogsFromDetail(): void {
        this.showAuditDetailView = false;
        this.selectedOrganizationAuditLog = null;
    }

    public closeOrganizationAuditDetails(): void {
        this.backToAuditLogsFromDetail();
    }

    public getAuditMetadataRows(audit: AdminOrganizationAuditDetail | null): Array<{ key: string; value: string }> {
        if (!audit) {
            return [];
        }

        const metadata = audit.metadata ?? this.tryParseAuditMetadataJson(audit.metadataJson);
        if (!metadata) {
            return [];
        }

        return Object.entries(metadata).map(([key, value]) => ({
            key,
            value: value == null ? '—' : String(value)
        }));
    }

    public getAuditDateDisplay(value: string | undefined): string {
        if (!value) {
            return '—';
        }

        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? value : parsedDate.toLocaleString();
    }

    public getAuditTargetDisplay(audit: AdminOrganizationAuditLog | AdminOrganizationAuditDetail | null): string {
        if (!audit) {
            return '—';
        }

        const targetType = audit.targetType || 'Unknown target';
        const targetId = audit.targetId || '—';
        return `${targetType} · ${targetId}`;
    }

    public getAuditActionSeverity(action: string | undefined): 'success' | 'warning' | 'danger' | 'info' {
        const normalizedAction = (action || '').trim().toLowerCase();
        if (!normalizedAction) {
            return 'info';
        }

        const [entityName, actionName] = normalizedAction.split('.');

        if (entityName === 'member') {
            if (actionName === 'invited' || actionName === 'created' || actionName === 'added' || actionName === 'restored') {
                return 'success';
            }

            if (actionName === 'updated' || actionName === 'edited' || actionName === 'changed' || actionName === 'rolechanged') {
                return 'warning';
            }

            if (actionName === 'suspended' || actionName === 'deleted' || actionName === 'removed' || actionName === 'revoked') {
                return 'danger';
            }

            // Default Member.* actions to warning because they usually impact access.
            return 'warning';
        }

        if (entityName === 'organization') {
            if (actionName === 'created' || actionName === 'restored') {
                return 'success';
            }

            if (actionName === 'updated' || actionName === 'edited' || actionName === 'changed') {
                return 'warning';
            }

            if (actionName === 'deleted' || actionName === 'archived') {
                return 'danger';
            }
        }

        if (normalizedAction.includes('delete') || normalizedAction.includes('suspend') || normalizedAction.includes('remove') || normalizedAction.includes('revoke')) {
            return 'danger';
        }

        if (normalizedAction.includes('invite') || normalizedAction.includes('create') || normalizedAction.includes('add') || normalizedAction.includes('restore')) {
            return 'success';
        }

        if (normalizedAction.includes('update') || normalizedAction.includes('change') || normalizedAction.includes('edit')) {
            return 'warning';
        }

        return 'info';
    }

    public getAuditTargetSeverity(targetType: string | null | undefined): 'success' | 'warning' | 'danger' | 'info' {
        const normalizedTargetType = (targetType || '').toLowerCase();

        if (normalizedTargetType.includes('member') || normalizedTargetType.includes('user')) {
            return 'warning';
        }

        if (normalizedTargetType.includes('organization')) {
            return 'success';
        }

        return 'info';
    }

    public backToOrganizations(): void {
        this.showMembersView = false;
        this.showAuditView = false;
        this.showAuditDetailView = false;
    }

    public isOrganizationSoftDeleted(organization: AdminOrganization | null | undefined): boolean {
        return !!organization?.isDeleted;
    }

    public getSoftDeleteIcon(organization: AdminOrganization): string {
        return this.isOrganizationSoftDeleted(organization) ? 'pi pi-undo' : 'pi pi-archive';
    }

    public getSoftDeleteTooltip(organization: AdminOrganization): string {
        return this.isOrganizationSoftDeleted(organization)
            ? 'Restore organization'
            : 'Archive organization';
    }

    public getSoftDeleteLabel(organization: AdminOrganization): string {
        return this.isOrganizationSoftDeleted(organization) ? 'Restore' : 'Archive';
    }

    public getSoftDeleteButtonClass(organization: AdminOrganization): string {
        return this.isOrganizationSoftDeleted(organization)
            ? 'p-button-secondary soft-delete-restorable'
            : 'p-button-warning';
    }

    public get selectedOrganizationActionButtons(): ActionButton[] {
        if (!this.selectedOrganization) {
            return [];
        }

        return [
            {
                label: this.isOrganizationSoftDeleted(this.selectedOrganization) ? 'Restore' : 'Archive',
                type: this.isOrganizationSoftDeleted(this.selectedOrganization) ? 'restore' : 'archive',
                color: this.isOrganizationSoftDeleted(this.selectedOrganization) ? 'secondary' : 'warning',
                outlined: true,
                size: 'small'
            },
            {
                label: 'Delete',
                type: 'hardDelete',
                color: 'danger',
                outlined: false,
                size: 'small'
            }
        ];
    }

    public handleSelectedOrganizationAction(event: { type?: string }): void {
        if (!event?.type || !this.selectedOrganization) {
            return;
        }

        switch (event.type) {
            case 'archive':
            case 'restore':
                void this.onSoftDeleteAction(this.selectedOrganization);
                break;
            case 'hardDelete':
                this.openOrganizationDeleteDialog(this.selectedOrganization, 'hard');
                break;
            default:
                break;
        }
    }

    public async onSoftDeleteAction(organization: AdminOrganization): Promise<void> {
        if (this.isOrganizationSoftDeleted(organization)) {
            const response = await this.adminPanelService.restoreOrganization(organization.id, { handleError: true });

            if (!response.success) {
                return;
            }

            this.organizations = this.organizations.map(item =>
                item.id === organization.id
                    ? { ...item, isDeleted: false, deletedDate: null, deletedByUserId: null, status: 'Active' }
                    : item
            );

            if (this.selectedOrganization?.id === organization.id) {
                this.selectedOrganization = {
                    ...this.selectedOrganization,
                    isDeleted: false,
                    deletedDate: null,
                    deletedByUserId: null,
                    status: 'Active'
                };
            }

            this.utils.successMessage(
                MessageType.TOAST,
                'Organization restored',
                `${organization.name || 'The organization'} has been restored.`
            );
            return;
        }

        this.openOrganizationDeleteDialog(organization, 'soft');
    }

    public openOrganizationDeleteDialog(organization: AdminOrganization, mode: 'soft' | 'hard'): void {
        this.organizationToDelete = organization;
        this.organizationDeleteMode = mode;
        this.hardDeleteConfirmationInput = '';
        this.refreshOrganizationDeleteActionButtons();
        this.showOrganizationDeleteConfirmDialog = true;
    }

    public async handleOrganizationDelete(event: { type?: string }): Promise<void> {
        if (event?.type !== 'confirm' || !this.organizationToDelete?.id) {
            this.closeOrganizationDeleteDialog();
            return;
        }

        if (this.organizationDeleteMode === 'hard' && this.hardDeleteConfirmationInput.trim().toUpperCase() !== 'CONFIRM') {
            return;
        }

        this.setActionButtonsDisabled(this.organizationDeleteActionButtons, true);

        try {
            const organization = this.organizationToDelete;
            const response = this.organizationDeleteMode === 'soft'
                ? await this.adminPanelService.softDeleteOrganization(organization.id, { handleError: true })
                : await this.adminPanelService.hardDeleteOrganization(organization.id, { handleError: true });

            if (!response.success) {
                return;
            }

            if (this.organizationDeleteMode === 'soft') {
                const deletedDate = new Date().toISOString();
                this.organizations = this.organizations.map(item =>
                    item.id === organization.id
                        ? { ...item, isDeleted: true, deletedDate, deletedByUserId: null, status: 'Deleted' }
                        : item
                );

                if (this.selectedOrganization?.id === organization.id) {
                    this.selectedOrganization = {
                        ...this.selectedOrganization,
                        isDeleted: true,
                        deletedDate,
                        deletedByUserId: null,
                        status: 'Deleted'
                    };
                }

                this.utils.successMessage(
                    MessageType.TOAST,
                    'Organization soft deleted',
                    `${organization.name || 'The organization'} has been marked as deleted.`
                );
                return;
            }

            this.organizations = this.organizations.filter(item => item.id !== organization.id);
            this.utils.successMessage(
                MessageType.TOAST,
                'Organization hard deleted',
                `${organization.name || 'The organization'} has been permanently removed.`
            );

            this.selectedOrganization = null;
            this.organizationMembers = [];
            this.showMembersView = false;
            this.showAuditView = false;
            this.showAuditDetailView = false;
            await this.loadOrganizations(true);
        } finally {
            this.setActionButtonsDisabled(this.organizationDeleteActionButtons, false);
            this.refreshOrganizationDeleteActionButtons();
            this.closeOrganizationDeleteDialog();
        }
    }

    public openMemberDeleteDialog(member: AdminOrganizationMember): void {
        this.memberToDelete = member;
        this.memberDeleteConfirmationInput = '';
        this.refreshMemberDeleteActionButtons();
        this.showMemberDeleteConfirmDialog = true;
    }

    public async handleMemberDelete(event: { type?: string }): Promise<void> {
        if (event?.type !== 'confirm' || !this.selectedOrganization?.id || !this.memberToDelete?.userId) {
            this.closeMemberDeleteDialog();
            return;
        }

        this.setActionButtonsDisabled(this.memberDeleteActionButtons, true);

        try {
            const organization = this.selectedOrganization;
            const member = this.memberToDelete;
            const response = await this.adminPanelService.hardDeleteOrganizationUser(organization.id, member.userId, { handleError: true });

            if (!response.success) {
                return;
            }

            this.organizationMembers = this.organizationMembers.filter(item => item.userId !== member.userId);
            this.organizations = this.organizations.map(item =>
                item.id === organization.id
                    ? { ...item, totalMembers: Math.max(0, Number(item.totalMembers ?? this.organizationMembers.length + 1) - 1) }
                    : item
            );

            this.utils.successMessage(
                MessageType.TOAST,
                'Member deleted',
                `${member.email || member.name || 'The member'} has been permanently removed from ${organization.name || 'the organization'}.`
            );
        } finally {
            this.setActionButtonsDisabled(this.memberDeleteActionButtons, false);
            this.closeMemberDeleteDialog();
        }
    }

    public get organizationDeleteDialogTitle(): string {
        return this.organizationDeleteMode === 'soft' ? 'Archive Organization' : 'Permanently Delete Organization';
    }

    public get organizationDeleteDialogType(): 'warning' | 'delete' {
        return this.organizationDeleteMode === 'soft' ? 'warning' : 'delete';
    }

    public get organizationDeleteMessage(): string {
        if (!this.organizationToDelete) {
            return '';
        }

        if (this.organizationDeleteMode === 'soft') {
            return `Are you sure you want to archive ${this.organizationToDelete.name || 'this organization'}? Members will lose access, but the record will remain available for audit and follow-up actions.`;
        }

        return `This is permanent. To confirm, type CONFIRM in the box below and click Continue to permanently remove ${this.organizationToDelete.name || 'this organization'}.`;
    }

    public get memberDeleteMessage(): string {
        if (!this.memberToDelete || !this.selectedOrganization) {
            return '';
        }

        return `Are you sure you want to permanently delete ${this.memberToDelete.email || this.memberToDelete.name || 'this member'} from ${this.selectedOrganization.name || 'this organization'}? Type CONFIRM below to proceed, this action cannot be undone.`;
    }

    public getOrganizationStatus(organization: AdminOrganization): string {
        if (organization?.isDeleted) {
            return 'Deleted';
        }

        return organization?.subscriptionStatus || organization?.status || 'Active';
    }

    public getOrganizationStatusSeverity(organization: AdminOrganization): 'success' | 'warning' | 'danger' | 'info' {
        const normalizedStatus = this.getOrganizationStatus(organization).toLowerCase();

        if (normalizedStatus.includes('delete')) {
            return 'danger';
        }

        if (normalizedStatus.includes('trial') || normalizedStatus.includes('pending')) {
            return 'warning';
        }

        if (normalizedStatus.includes('cancel')) {
            return 'info';
        }

        return 'success';
    }

    public getMemberStatusSeverity(member: AdminOrganizationMember): 'success' | 'warning' | 'danger' | 'info' {
        const normalizedStatus = (member?.status || '').toLowerCase();

        if (normalizedStatus === 'active') {
            return 'success';
        }

        if (normalizedStatus === 'invited' || normalizedStatus === 'pending') {
            return 'warning';
        }

        if (normalizedStatus === 'suspended' || normalizedStatus === 'deleted') {
            return 'danger';
        }

        return 'info';
    }

    private closeOrganizationDeleteDialog(): void {
        this.showOrganizationDeleteConfirmDialog = false;
        this.organizationToDelete = null;
        this.organizationDeleteMode = 'soft';
        this.hardDeleteConfirmationInput = '';
        this.refreshOrganizationDeleteActionButtons();
    }

    private closeMemberDeleteDialog(): void {
        this.showMemberDeleteConfirmDialog = false;
        this.memberToDelete = null;
        this.memberDeleteConfirmationInput = '';
        this.refreshMemberDeleteActionButtons();
    }

    private setActionButtonsDisabled(buttons: ActionButton[], disabled: boolean): void {
        buttons.forEach(button => {
            button.disabled = disabled;
        });
    }

    private tryParseAuditMetadataJson(metadataJson: string | null | undefined): Record<string, unknown> | null {
        if (!metadataJson) {
            return null;
        }

        try {
            const parsed = JSON.parse(metadataJson);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? parsed as Record<string, unknown>
                : null;
        } catch {
            return null;
        }
    }

    private getDisplayValue(value: string | number | null | undefined, isDate = false): string {
        if (value == null || value === '') {
            return 'Not provided';
        }

        if (!isDate) {
            return `${value}`;
        }

        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? `${value}` : parsedDate.toLocaleString();
    }


}
